import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface VolcesSeedream45MultiBlendParams {
  /**
   * 文本提示词，用于描述融合后的图片内容
   */
  prompt: string;
  /**
   * 输入图片列表，支持 URL 地址或 Base64 编码的图片数据（必须为列表格式，至少2张，最多14张图片）
   * 支持 image 或 images 参数名
   */
  image?: string[];
  /**
   * 输入图片列表（别名），与 image 参数相同
   */
  images?: string[];
  /**
   * 图片尺寸，例如："2K"、"1024x1024" 等
   */
  size?: string;
  /**
   * 是否添加水印，默认值为 false
   * @default false
   */
  watermark?: boolean;
  /**
   * 响应格式，可选值："url" 或 "b64_json"，默认值为 "url"
   * @default "url"
   * @values url | b64_json
   */
  response_format?: 'url' | 'b64_json';
  /**
   * 提示词优化选项
   */
  optimize_prompt_options?: Record<string, any>;
}

export interface VolcesSeedream45MultiBlendResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * volces_seedream_4_5_multi_blend
 *
 * API Documentation: https://docs.byteplus.com/en/docs/ModelArk/1541523
 * 
 * Volces Seedream 4.5 多图融合接口，输入多张图片融合为单张输出图片（需要至少2张图片，最多支持14张）。自动启用融合模式 (sequential_image_generation=disabled)。
 *
 * @example
 * ```typescript
 * import { initSeacloud, volcesSeedream45MultiBlend } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await volcesSeedream45MultiBlend({
 *   // 参数见 VolcesSeedream45MultiBlendParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 VolcesSeedream45MultiBlendParams 接口定义
 * @returns 生成的资源列表
 */
export async function volcesSeedream45MultiBlend(
  params: VolcesSeedream45MultiBlendParams,
): Promise<VolcesSeedream45MultiBlendResource[]> {
  const client = await getClient();

  // 支持 images 和 image 参数名（优先使用 image，兼容 images）
  const imageList = params.image || params.images;

  if (!imageList || !Array.isArray(imageList)) {
    throw new Error('Parameter "image" or "images" is required and must be an array');
  }

  if (imageList.length < 2 || imageList.length > 14) {
    throw new Error('Image array must contain between 2 and 14 images');
  }

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  // 构建请求参数，确保使用 image 参数名
  const requestParams = {
    ...params,
    image: imageList,
  };

  // 删除 images 参数避免重复
  delete requestParams.images;

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'volces_seedream_4_5_multi_blend',
      input: [{ params: requestParams }],
    },
    pollingOptions,
  );

  const resources: VolcesSeedream45MultiBlendResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
